import { Label } from '@fluentui/react';
import { SloDefinitions, TelemetryEventType, logService } from 'npo-common';
import * as React from 'react';
import { ORG_TYPE_NONPROFIT } from 'src/common/Constants';
import { IUploadResult, UploadErrorCode } from 'src/dataServices/DocumentService';
import localeService from 'src/dataServices/LocaleService';
import { NonprofitStatus } from 'src/state/nonprofit/types';
import { IProfileDocument } from 'src/state/profile/types';
import FieldValidationError from 'src/components/profilev2/FieldValidationError';
import FileUploader from 'src/components/profilev2/FileUploader';
import Text from 'src/components/text/Text';
import './MultiDocUpload.scss';

interface IMultiDocUploadProps {
  isEditMode?: boolean;
  organizationType?: string;
  documents?: IProfileDocument[];
  showDocumentRequiredError?: boolean;
  onDocumentsChange?: (documents: IProfileDocument[]) => void;
  nonprofitId?: string;
  nonprofitStatus?: NonprofitStatus;
}

const MultiDocUpload = ({
  isEditMode,
  organizationType,
  documents,
  showDocumentRequiredError,
  onDocumentsChange,
  nonprofitId,
  nonprofitStatus
}: IMultiDocUploadProps): JSX.Element | null => {
  const [uploadErrorCode, setUploadErrorCode] = React.useState<UploadErrorCode | null>(null);

  if (organizationType !== ORG_TYPE_NONPROFIT) {
    return null;
  }

  const files = (documents || []).filter((document) => Boolean(document?.name));
  const showFilesSection = Boolean(isEditMode) || files.length > 0;

  const onDeleteDocument = (documentToDelete: IProfileDocument) => {
    if (!onDocumentsChange) {
      return;
    }

    const nextDocuments = files.filter((document) => {
      if (documentToDelete.id && document.id) {
        return document.id !== documentToDelete.id;
      }

      return document.name !== documentToDelete.name;
    });

    try {
      logService.logEvent({
        Name: 'DocumentDeleted',
        EventType: TelemetryEventType.PrimaryWorkflow,
        SloId: SloDefinitions.NonprofitProfileUpdate,
        Properties: {
          nonprofitId: nonprofitId ?? '',
          nonprofitStatus: nonprofitStatus ?? '',
          documentName: documentToDelete.name ?? ''
        }
      });
    } catch {
      // telemetry must never break the UI
    }

    onDocumentsChange(nextDocuments);
  };

  const renderEvidenceDescription = () => {
    const description = localeService.getText('ProfileV2', 'evidenceDescription') || '';
    const nonprofitDefinitionsUrl = localeService.getText('ProfileV2', 'nonprofitDefinitions') || '#';

    const parts = description.match(/^(.*)<0>(.*)<\/0>(.*)$/);
    if (!parts) {
      return <>{description}</>;
    }

    return (
      <>
        {parts[1]}
        <a href={nonprofitDefinitionsUrl} target="_blank" rel="noreferrer">
          {parts[2]}
        </a>
        {parts[3]}
      </>
    );
  };

  const onUploadComplete = (result: IUploadResult) => {
    setUploadErrorCode(null);

    if (!result.success && (result.uploadErrorCode === 'fileTooLarge' || result.uploadErrorCode === 'duplicateFile' || result.uploadErrorCode === 'uploadFailed')) {
      setUploadErrorCode(result.uploadErrorCode);
      return;
    }

    if (!result.success || !onDocumentsChange) {
      return;
    }

    const newDocument: IProfileDocument = {
      id: result.documentId,
      name: result.documentName,
    };

    try {
      logService.logEvent({
        Name: 'DocumentUploaded',
        EventType: TelemetryEventType.PrimaryWorkflow,
        SloId: SloDefinitions.NonprofitProfileUpdate,
        Properties: {
          nonprofitId: nonprofitId ?? '',
          nonprofitStatus: nonprofitStatus ?? '',
          documentName: result.documentName ?? ''
        }
      });
    } catch {
      // telemetry must never break the UI
    }

    onDocumentsChange([...files, newDocument]);
  };

  return (
    <div className="supporting-documents">
      <div className="supporting-documents__intro">
        <p className="supporting-documents__label">{localeService.getText('ProfileV2', 'evidence')}</p>
        <p className="supporting-documents__description">{renderEvidenceDescription()}</p>
      </div>

      {showFilesSection ? (
        <div className="supporting-documents__files">
          <div className="field">
            <Label tabIndex={-1} id="supportingDocumentsDocumentName">
              <Text category="ProfileV2" id="documentNameLabel" />
            </Label>
          </div>
          {files.length > 0 && (
            <ul aria-labelledby="supportingDocumentsDocumentName" className="supporting-documents__list">
              {files.map((document) => (
                <li className="supporting-documents__item" key={document.id || document.name}>
                  <span className="supporting-documents__filename">
                    <b>{document.name}</b>
                  </span>
                  {isEditMode && (
                    <button
                      type="button"
                      className="action-trigger glyph-prepend glyph-prepend-delete supporting-documents__delete"
                      aria-label={localeService.getText('ProfileV2', 'Delete')}
                      title={localeService.getText('ProfileV2', 'Delete')}
                      onClick={() => onDeleteDocument(document)}
                    />
                  )}
                </li>
              ))}
            </ul>
          )}
          {isEditMode && (
            <FileUploader
              existingDocumentNames={files.map((document) => document.name).filter((name): name is string => Boolean(name))}
              onUploadComplete={onUploadComplete}
            />
          )}
          {uploadErrorCode === 'fileTooLarge' && <FieldValidationError localeErrorTextId="uploadError" />}
          {uploadErrorCode === 'duplicateFile' && <FieldValidationError localeErrorTextId="duplicateFileError" />}
          {uploadErrorCode === 'uploadFailed' && <FieldValidationError localeErrorTextId="uploadError" />}
          {showDocumentRequiredError && <FieldValidationError localeErrorTextId="documentRequiredError" />}
        </div>
      ) : (
        <p className="supporting-documents__empty">
          <Text category="ProfileV2" id="noSupportingDocuments" />
        </p>
      )}
    </div>
  );
};

export default MultiDocUpload;
